import { FC, useState } from 'react';
import { MdClose } from 'react-icons/md';
import { Button, Modal } from 'components/core';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Styled } from '.';
import { ChangeDeliveryDateModalProps } from './ChangeShipmentDateModal.types';
import { useUpdateSubscriptionNextDelivery } from 'reactQuery/mutations';
import { formatDateToYYYYMMDD } from 'utils';
import { useI18n } from 'contexts/i18n';

const ChangeDeliveryDateModal: React.FC<ChangeDeliveryDateModalProps> = ({
  subscriptionId,
  onClose,
  currentDeliveryDate,
  lastDayToChangeShipment,
}) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isConfirmationVisible, setIsConfirmationVisible] = useState(true);
  const [isPermanentChange, setIsPermanentChange] = useState(false);
  const currentDelivery = new Date(currentDeliveryDate);
  const minDate = new Date();
  const i18n = useI18n();
  minDate.setDate(minDate.getDate() + 1);
  const maxDate = new Date(lastDayToChangeShipment);

  const updateSubscriptionNextDeliveryMutation =
    useUpdateSubscriptionNextDelivery();

  const permanentChangeDate = (value: boolean) => {
    setIsPermanentChange(value);
    setIsConfirmationVisible(false);
  };

  const handleChangeDeliveryDate = async () => {
    if (!selectedDate) return;
    await updateSubscriptionNextDeliveryMutation.mutateAsync({
      subscriptionId: subscriptionId,
      nextOccurrenceAt: formatDateToYYYYMMDD(selectedDate),
      isPermanentChange: isPermanentChange,
    });
  };

  return (
    <>
      {isConfirmationVisible ? (
        <Modal>
          <Styled.Container>
            <Styled.ModalHeader>
              <Styled.Title>{i18n['Change your upcoming shipment']}</Styled.Title>
              <Styled.CloseButton onClick={onClose}>
                <MdClose size={24} />
              </Styled.CloseButton>
            </Styled.ModalHeader>
            <Styled.ModalBody>
              <Styled.SubText>
                {i18n['Would you like to change only the next shipment date or update all future deliveries?']}
              </Styled.SubText>
            </Styled.ModalBody>
            <Styled.ButtonWrapper>
              <Button size="md" onClick={() => permanentChangeDate(false)}>
                {i18n['Only this shipment']}
              </Button>
              <Button size="md" onClick={() => permanentChangeDate(true)}>
                {i18n['All future shipments']}
              </Button>
            </Styled.ButtonWrapper>
          </Styled.Container>
        </Modal>
      ) : (
        <Modal>
          <Styled.Container>
            <Styled.ModalHeader>
              <Styled.Title>{i18n['Change your upcoming shipment']}</Styled.Title>
              <Styled.CloseButton onClick={onClose}>
                <MdClose size={24} />
              </Styled.CloseButton>
            </Styled.ModalHeader>
            <Styled.ModalBody>
              <Styled.SubText>
                {i18n['Select a new shipment day in the calendar provided.']}
              </Styled.SubText>
              <Styled.SubText>
                {i18n['Current scheduled shipment day is']}{' '}
                <Styled.HighlightedDate>
                  {currentDelivery.toDateString()}
                </Styled.HighlightedDate>
              </Styled.SubText>
              <Styled.CalendarWrapper>
                <DatePicker
                  selected={selectedDate}
                  onChange={date => setSelectedDate(date || selectedDate)}
                  inline
                  minDate={minDate}
                  maxDate={maxDate}
                />
              </Styled.CalendarWrapper>
              {selectedDate && (
                <Styled.SubText>
                  {i18n['New shipment date']}{' '}
                  <Styled.HighlightedDate>
                    {selectedDate.toDateString()}
                  </Styled.HighlightedDate>
                </Styled.SubText>
              )}
            </Styled.ModalBody>
            <Styled.ButtonWrapper>
              <Button size="md" variant="secondary" onClick={onClose}>
                {i18n['Cancel']}
              </Button>
              <Button
                size="md"
                onClick={handleChangeDeliveryDate}
                disabled={!selectedDate}
              >
                {i18n['Confirm']}
              </Button>
            </Styled.ButtonWrapper>
          </Styled.Container>
        </Modal>
      )}
    </>
  );
};

export default ChangeDeliveryDateModal;
